import React, { useEffect, useCallback, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/src/store/hooks'
import {
    Container,
    Typography,
    Spacer,
    Dropdown,
    Calendar,
    Button
} from '@ct-design/lib'
import SearchableDropdown from '@/src/ct-design/lib/SearchableDropdown'
import { Item } from '@/src/ct-design/lib/Dropdown/type'
import { addDays } from 'date-fns'

// Constants
import {
    nationalityList,
    travelDetailsList,
    NATIONALITY,
    DESTINATION,
    NATIONALITY_TITLE,
    NATIONALITY_DROPDOWN_PLACEHOLDER,
    DESTINATION_TITLE,
    DESTINATION_DROPDOWN_PLACEHOLDER
} from '@/src/constants/visa/travelDetails'
import {
    numOfTravellersList,
    numOfChildTravellerList
} from '@/src/constants/visa/visaRequirements'

// Store imports
import {
    getTravelDetails,
    setTravelDetails
} from '@/src/store/slices/visaSlice/travelDetailsSlice'
import {
    getVisaRequirementsState,
    fetchVisaRequirements,
    setVisaType,
    setProcessingSpeed,
    setDateOfArrival,
    setPaxCount
} from '@/src/store/slices/visaSlice/visaRequirementsSlice'
import { getVisaErrorFields } from '@/src/store/slices/visaSlice/visaErrorSlice'

// Hooks
import useVisaFormValidation from '@/src/hooks/useVisaFormValidation'
import { getDisplayValue } from '@/src/utils/visa'
import { isEmpty } from '@/src/utils/helper'
import { DeviceType } from '@/src/types'

interface VisaDetailsStepProps {
    deviceType?: DeviceType
    onContinue?: () => void
    isButtonLoading?: boolean
}

const VisaDetailsStep: React.FC<VisaDetailsStepProps> = ({
    deviceType = 'DESKTOP',
    onContinue,
    isButtonLoading = false
}) => {
    const dispatch = useAppDispatch()
    const { clearError, validateTravelDetails, validateVisaRequirements } =
        useVisaFormValidation()
    const error = useAppSelector(getVisaErrorFields)

    // Travel Details state
    const { nationality, destination } = useAppSelector(getTravelDetails)

    // Visa Requirements state
    const {
        isLoading,
        visaType,
        processingSpeed,
        dateOfArrival,
        paxDetails,
        fareRef
    } = useAppSelector(getVisaRequirementsState)

    const { adults, children } = paxDetails

    // Get visa type list (exact same as old flow)
    const visaList = useMemo(() => {
        if (Array.isArray(visaType.options)) {
            return visaType.options.map(({ visaType }, index) => ({
                item: visaType,
                key: `${index}`
            }))
        }
        return []
    }, [visaType.options])

    // Get processing speeds list and selected visa type (exact same as old flow)
    const { selectedVisaType, processingSpeedsList } = useMemo(() => {
        const { selectedOption, options } = visaType
        const { processingSpeeds } = selectedOption || {}

        let processingSpeedsList: Item[] = []
        if (Array.isArray(processingSpeeds)) {
            processingSpeedsList = processingSpeeds.map(
                (processingSpeed, index) => ({
                    item: processingSpeed.type,
                    key: `${index}`
                })
            )
        }

        const selectedVisa = {
            type: selectedOption ? selectedOption.visaType : '',
            index: options?.findIndex(
                (option) => option.visaType === selectedOption?.visaType
            )
        }

        return {
            selectedVisaType: selectedVisa,
            processingSpeedsList
        }
    }, [visaType])

    // Get selected processing speed (exact same as old flow)
    const selectedProcessingSpeed = useMemo(() => {
        const selected = {
            type: '',
            index: -1
        }
        if (
            !isEmpty(processingSpeed) &&
            Array.isArray(visaType.selectedOption?.processingSpeeds)
        ) {
            selected.type = processingSpeed?.type || ''
            selected.index = visaType.selectedOption.processingSpeeds.findIndex(
                (speed) => speed.type === processingSpeed?.type
            )
        }
        return selected
    }, [processingSpeed, visaType.selectedOption])

    // Check if both nationality and destination are selected
    const areFieldsSelected = useMemo(() => {
        return !isEmpty(nationality.value) && !isEmpty(destination.value)
    }, [nationality.value, destination.value])

    // Only show loading shimmer when actually fetching data, not when fields are just disabled
    const showVisaFieldsLoading = useMemo(() => {
        return areFieldsSelected && isLoading
    }, [areFieldsSelected, isLoading])

    // Handle Travel Details field selection
    const handleTravelDetailsSelect = useCallback(
        (
            type: typeof NATIONALITY | typeof DESTINATION,
            item: Item,
            sourceList: Item[]
        ) => {
            const index = sourceList.findIndex(
                (element) => element.key === item.key
            )
            dispatch(
                setTravelDetails({
                    [type]: { value: index !== -1 ? item.key : '', index }
                })
            )
            clearError(type)

            // Check if both fields are now selected to trigger API call
            const updatedNationality =
                type === NATIONALITY ? item.key : nationality.value
            const updatedDestination =
                type === DESTINATION ? item.key : destination.value

            if (!isEmpty(updatedNationality) && !isEmpty(updatedDestination)) {
                // Trigger country metadata API call
                dispatch(
                    fetchVisaRequirements({
                        nationalityCode: updatedNationality,
                        destinationCountryCode: updatedDestination,
                        language: 'en',
                        deviceType
                    })
                )
            }
        },
        [dispatch, clearError, nationality.value, destination.value, deviceType]
    )

    // Calculate estimated price (keeping for potential future use)
    // const estimatedPrice = useMemo(() => {
    //     if (!isEmpty(processingSpeed) && processingSpeed && adults > 0) {
    //         const { paxFareTypeKeys } = processingSpeed
    //         const adultFareKey = paxFareTypeKeys['ADT']
    //         const childFareKey = paxFareTypeKeys['CHD']
    //         const perAdultPrice = fareRef[adultFareKey]?.startingPrice || 0
    //         const perChildPrice = fareRef[childFareKey]?.startingPrice || 0
    //         return adults * perAdultPrice + children * perChildPrice
    //     }
    //     return 0
    // }, [processingSpeed, adults, children, fareRef])

    // Handle visa type selection
    const handleVisaTypeSelect = useCallback(
        (item: Item) => {
            const index = Number(item.key)
            const selectedVisaType = visaType.options[index]
            const { processingSpeeds } = selectedVisaType

            dispatch(setVisaType(selectedVisaType))
            if (Array.isArray(processingSpeeds)) {
                dispatch(setProcessingSpeed(processingSpeeds[0]))
            } else {
                dispatch(setProcessingSpeed(null))
            }
            clearError('visaType')
        },
        [dispatch, visaType.options, clearError]
    )

    // Handle processing speed selection
    const handleProcessingSpeedSelect = useCallback(
        (item: Item) => {
            const { selectedOption } = visaType
            if (!isEmpty(selectedOption)) {
                const processingSpeedIndex = Number(item.key)
                const selectedProcessingSpeed =
                    selectedOption?.processingSpeeds[processingSpeedIndex]
                if (selectedProcessingSpeed) {
                    dispatch(setProcessingSpeed(selectedProcessingSpeed))
                }
            }
            clearError('processingSpeed')
        },
        [dispatch, visaType, clearError]
    )

    // Handle date change
    const handleDateChange = useCallback(
        (date: Date) => {
            const newDate = new Date(date.setHours(12, 0, 0, 0))
            dispatch(setDateOfArrival(newDate.toISOString()))
            clearError('dateOfArrival')
        },
        [dispatch, clearError]
    )

    // Handle pax count change
    const handlePaxCountChange = useCallback(
        (type: 'adults' | 'children', value: number) => {
            const updatedPaxCount = {
                ...paxDetails,
                [type]: value
            }
            dispatch(setPaxCount(updatedPaxCount))
            clearError('paxDetails')
        },
        [dispatch, paxDetails, clearError]
    )

    // Handle Continue button click
    const handleContinue = useCallback(() => {
        // Validate travel details
        const travelDetailsValid = validateTravelDetails(
            nationality,
            destination
        )

        // Validate visa requirements
        const visaRequirementsValid = validateVisaRequirements()

        // If all validations pass, call the onContinue callback
        if (travelDetailsValid && visaRequirementsValid && onContinue) {
            onContinue()
        }
    }, [
        nationality,
        destination,
        validateTravelDetails,
        validateVisaRequirements,
        onContinue
    ])

    // Check if Continue button should be enabled
    const canContinue = useMemo(() => {
        return (
            !isEmpty(nationality.value) &&
            !isEmpty(destination.value) &&
            !isEmpty(visaType.selectedOption) &&
            !isEmpty(processingSpeed) &&
            !isEmpty(dateOfArrival) &&
            adults > 0 &&
            adults + children <= 10
        )
    }, [
        nationality.value,
        destination.value,
        visaType.selectedOption,
        processingSpeed,
        dateOfArrival,
        adults,
        children
    ])

    // Don't reset visa requirements data - we want to preserve it for persistence

    // Load visa requirements when nationality and destination are selected (like old flow)
    useEffect(() => {
        if (
            !isEmpty(nationality.value) &&
            !isEmpty(destination.value) &&
            visaType.options.length === 0 &&
            !isLoading
        ) {
            dispatch(
                fetchVisaRequirements({
                    nationalityCode: nationality.value,
                    destinationCountryCode: destination.value,
                    language: 'en',
                    deviceType
                })
            )
        }
    }, [])

    return (
        <Container className='flex flex-column'>
            {/* Travel Details Section */}
            <Container className='flex flex-row flex-between mb-6'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>{NATIONALITY_TITLE}</Typography>
                    <Spacer className='mb-2' />
                    <SearchableDropdown
                        dropDownValue={getDisplayValue(
                            nationality.value,
                            nationalityList
                        )}
                        placeHolder={NATIONALITY_DROPDOWN_PLACEHOLDER}
                        itemList={nationalityList}
                        onItemSelect={(item) =>
                            handleTravelDetailsSelect(
                                NATIONALITY,
                                item,
                                nationalityList
                            )
                        }
                        defaultSingleSelectedIndex={nationality.index}
                        hasError={!!error.nationality}
                        enableSearch={true}
                        searchPlaceholder='Search nationality...'
                        searchFilterFunction={(items, searchTerm) => {
                            return items.filter((item) =>
                                item.item
                                    .toLowerCase()
                                    .includes(searchTerm.toLowerCase())
                            )
                        }}
                    />
                    <Typography variant='P3' className='mt-1' color='warning'>
                        {error.nationality}
                    </Typography>
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>{DESTINATION_TITLE}</Typography>
                    <Spacer className='mb-2' />
                    <Dropdown
                        dropDownValue={getDisplayValue(
                            destination.value,
                            travelDetailsList
                        )}
                        placeHolder={DESTINATION_DROPDOWN_PLACEHOLDER}
                        itemList={travelDetailsList}
                        onItemSelect={(item) =>
                            handleTravelDetailsSelect(
                                DESTINATION,
                                item,
                                travelDetailsList
                            )
                        }
                        defaultSingleSelectedIndex={destination.index}
                        hasError={!!error.destination}
                    />
                    <Typography variant='P3' className='mt-1' color='warning'>
                        {error.destination}
                    </Typography>
                </Container>
            </Container>

            {/* Visa Requirements Section */}
            <Container className='flex flex-row flex-between mb-5'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Visa Type</Typography>
                    <Spacer className='mb-2' />
                    <Dropdown
                        dropDownValue={selectedVisaType.type}
                        placeHolder='Select visa type'
                        itemList={visaList}
                        onItemSelect={handleVisaTypeSelect}
                        defaultSingleSelectedIndex={selectedVisaType.index}
                        isLoading={showVisaFieldsLoading}
                        hasError={!!error.visaType}
                        disabled={!areFieldsSelected}
                    />
                    <Typography
                        variant='P3'
                        className='mt-1 pl-3'
                        color='warning'
                    >
                        {error.visaType}
                    </Typography>
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Processing Speed</Typography>
                    <Spacer className='mb-2' />
                    <Dropdown
                        dropDownValue={selectedProcessingSpeed.type}
                        placeHolder='Select processing speed'
                        itemList={processingSpeedsList}
                        onItemSelect={handleProcessingSpeedSelect}
                        defaultSingleSelectedIndex={
                            selectedProcessingSpeed.index
                        }
                        disabled={
                            !areFieldsSelected ||
                            processingSpeedsList.length === 0
                        }
                        isLoading={showVisaFieldsLoading}
                    />
                </Container>
            </Container>

            {/* Date and Travellers Section */}
            <Container className='flex flex-row flex-between mb-6'>
                <Container className='flex flex-column flex-1'>
                    <Typography variant='B2'>Date of Arrival</Typography>
                    <Spacer className='mb-2' />
                    <Calendar
                        initialDate={
                            selectedProcessingSpeed &&
                            processingSpeed?.arrival_days_range
                                ? addDays(
                                      new Date(),
                                      processingSpeed.arrival_days_range[0]
                                  )
                                : addDays(new Date(), 15)
                        }
                        selectedDate={
                            dateOfArrival ? new Date(dateOfArrival) : null
                        }
                        type='dateOfArrival'
                        onDateChange={(type, date) => handleDateChange(date)}
                        isLoading={false}
                        error={error.dateOfArrival}
                        minDate={
                            processingSpeed?.arrival_days_range
                                ? addDays(
                                      new Date(),
                                      processingSpeed.arrival_days_range[0]
                                  )
                                : null
                        }
                        maxDate={
                            processingSpeed?.arrival_days_range
                                ? addDays(
                                      new Date(),
                                      processingSpeed.arrival_days_range[1]
                                  )
                                : null
                        }
                        weekdayEnable={true}
                    />
                </Container>

                <Spacer className='mr-8' />

                <Container className='flex flex-row flex-between flex-1'>
                    <Container className='flex flex-column flex-1'>
                        <Typography variant='B2'>Adults Travelling</Typography>
                        <Spacer className='mb-2' />
                        <Dropdown
                            dropDownValue={adults.toString()}
                            placeHolder=''
                            itemList={numOfTravellersList}
                            onItemSelect={(item) =>
                                handlePaxCountChange('adults', Number(item.key))
                            }
                            defaultSingleSelectedIndex={adults - 1}
                            isLoading={false}
                            hasError={!!error.paxDetails}
                        />
                        <Typography
                            variant='P3'
                            className='mt-1'
                            color='warning'
                        >
                            {error.paxDetails}
                        </Typography>
                    </Container>

                    <Spacer className='mr-4' />

                    <Container className='flex flex-column flex-1'>
                        <Typography variant='B2'>
                            Children Travelling
                        </Typography>
                        <Spacer className='mb-2' />
                        <Dropdown
                            dropDownValue={children.toString()}
                            placeHolder=''
                            itemList={numOfChildTravellerList}
                            onItemSelect={(item) =>
                                handlePaxCountChange(
                                    'children',
                                    Number(item.key)
                                )
                            }
                            defaultSingleSelectedIndex={children}
                            isLoading={false}
                            hasError={!!error.paxDetails}
                        />
                    </Container>
                </Container>
            </Container>

            {/* Continue Button */}
            <Container className='flex flex-row flex-end'>
                <Button
                    color='secondary'
                    size='large'
                    minWidth='126px'
                    onClick={handleContinue}
                    isLoading={isButtonLoading}
                    disabled={!canContinue}
                >
                    Continue
                </Button>
            </Container>
        </Container>
    )
}

export default VisaDetailsStep
